import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { PageWrapper, AsideNavigation, Content } from 'components/Transactions';
import { Button, Col, Row } from 'components-antd';
import { Plus } from 'components/Icons';
import { LinkCard, LINK_CARD_VARIANTS, LinkModal } from './components';
import { ConfirmationDialog, Wrapper } from 'components';

import { getTransactionEffect } from 'store/effects';
import { getTransactionSelector } from 'store/selectors/transaction';
import { getTransactionLinks, deleteTransactionLink } from 'api/transactions';
import { isTransactionEmpty } from '../helper';
import { showErrorMessage } from 'helpers';

import styles from './styles.module.scss';

const EmptyLinkSVG = () => (
  <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g clipPath="url(#clip0_83737_79255)">
      <path
        d="M26.6686 37.3337C27.5376 38.2206 28.5748 38.9252 29.7196 39.4063C30.8643 39.8873 32.0935 40.1351 33.3352 40.1351C34.5769 40.1351 35.8062 39.8873 36.9509 39.4063C38.0957 38.9252 39.1329 38.2206 40.0019 37.3337L50.6686 26.667C52.4367 24.8989 53.43 22.5008 53.43 20.0004C53.43 17.4999 52.4367 15.1018 50.6686 13.3337C48.9005 11.5656 46.5024 10.5723 44.0019 10.5723C41.5014 10.5723 39.1034 11.5656 37.3352 13.3337L36.0019 14.667"
        stroke="#D6D6D7"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M37.3333 26.6666C36.4643 25.7797 35.4271 25.0751 34.2823 24.594C33.1376 24.113 31.9084 23.8652 30.6667 23.8652C29.425 23.8652 28.1957 24.113 27.051 24.594C25.9063 25.0751 24.869 25.7797 24 26.6666L13.3333 37.3333C11.5652 39.1014 10.5719 41.4994 10.5719 43.9999C10.5719 46.5004 11.5652 48.8985 13.3333 50.6666C15.1014 52.4347 17.4995 53.428 20 53.428C22.5005 53.428 24.8985 52.4347 26.6667 50.6666L28 49.3333"
        stroke="#D6D6D7"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>
    <defs>
      <clipPath id="clip0_83737_79255">
        <rect width="64" height="64" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

function getLinkType(url) {
  const result = {
    isGoogleDoc: false,
    isGoogleSheet: false,
    isGoogleSlides: false,
    isGooglePDF: false,
    isZillow: false,
    isDropbox: false,
    isHomes: false,
    isRedfin: false,
    isRealtor: false,
    isUnknown: false,
  };

  try {
    const parsedUrl = new URL(url);
    const host = parsedUrl.hostname;
    const path = parsedUrl.pathname;

    if (host.includes('docs.google.com')) {
      if (path.startsWith('/document/')) {
        result.isGoogleDoc = true;
      } else if (path.startsWith('/spreadsheets/')) {
        result.isGoogleSheet = true;
      } else if (path.startsWith('/presentation/')) {
        result.isGoogleSlides = true;
      } else if (path.endsWith('.pdf') || path.includes('/file/d/')) {
        result.isGooglePDF = true;
      } else {
        result.isUnknown = true;
      }
    } else if (host.includes('zillow.com')) {
      result.isZillow = true;
    } else if (host.includes('dropbox.com')) {
      result.isDropbox = true;
    } else if (host.includes('homes.com')) {
      result.isHomes = true;
    } else if (host.includes('realtor.com')) {
      result.isRealtor = true;
    } else if (host.includes('redfin.com')) {
      result.isRedfin = true;
    } else {
      result.isUnknown = true;
    }
  } catch (e) {
    result.isUnknown = true;
  }

  return result;
}

export const TransactionLinks = () => {
  const dispatch = useDispatch();
  const [showNewLinkModal, setShowNewLinkModal] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState(false);
  const [confirmationLoading, setConfirmationLoading] = useState(false);
  const [trxLinks, setTrxLinks] = useState<any>();
  const [selectedLinkToEdit, setSelectedLinkToEdit] = useState<any>();
  const [loading, setLoading] = useState(false);
  const params: { id?: string } = useParams();
  const { transaction, isIdle, isPending } = useSelector(getTransactionSelector);

  const cardTypes = [
    LINK_CARD_VARIANTS.RED,
    LINK_CARD_VARIANTS.GREEN,
    LINK_CARD_VARIANTS.BLUE,
    LINK_CARD_VARIANTS.PURPLE,
    LINK_CARD_VARIANTS.CONFETTI_BLUE,
    LINK_CARD_VARIANTS.ORANGE,
    LINK_CARD_VARIANTS.GRAY,
  ];

  useEffect(() => {
    if (isTransactionEmpty(transaction))
      dispatch(
        getTransactionEffect(
          { id: params?.id, include: 'milestones,participants,transactioncommission' },
          { silent: !isIdle },
        ),
      );

    fetchTransactionLinks();
  }, []);

  const fetchTransactionLinks = async () => {
    setLoading(true);

    try {
      const resp = await getTransactionLinks(params?.id);
      setTrxLinks(resp?.data?.value || []);
      setSelectedLinkToEdit(null);
    } catch (err) {
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteLink = async () => {
    setConfirmationLoading(true);

    try {
      const response = await deleteTransactionLink(params?.id, selectedLinkToEdit?.Id);

      if (response.status === 200) {
        setDeleteConfirmation(false);
        fetchTransactionLinks();
        setSelectedLinkToEdit(null);
      }
    } catch (err) {
      setConfirmationLoading(false);
      showErrorMessage('Unable to delete');
    } finally {
      setConfirmationLoading(false);
    }
  };

  return (
    <PageWrapper>
      <AsideNavigation />
      <Content bgGray>
        <div className={styles.transactionLinksContainer}>
          <div className={styles.header}>
            <h1 className={styles.title}>Links</h1>
            <Button
              variant="secondary"
              className={styles.newBtn}
              onClick={() => setShowNewLinkModal(true)}
            >
              <Plus color="#fff" className={styles.plusIcon} /> New
            </Button>
          </div>
          <Wrapper isPending={isPending || loading}>
            <div className={styles.content}>
              <Row gutter={32}>
                {trxLinks?.length ? (
                  trxLinks?.map((item, idx) => (
                    <Col key={idx} span={8}>
                      <LinkCard
                        name={item?.Name}
                        desc={item?.Description}
                        access={item?.Access}
                        link={item?.Address}
                        variant={cardTypes?.[idx % cardTypes?.length]}
                        onEdit={() => {
                          setSelectedLinkToEdit(item);
                          setShowNewLinkModal(true);
                        }}
                        onDelete={() => {
                          setSelectedLinkToEdit(item);
                          setDeleteConfirmation(true);
                        }}
                        createdBy={item?.CreatedBy}
                        {...getLinkType(item?.Address)}
                      />
                    </Col>
                  ))
                ) : (
                  <div className={styles.emptyText}>
                    <EmptyLinkSVG />
                    <span>You don&apos;t have any links yet.</span>
                  </div>
                )}
              </Row>
            </div>
          </Wrapper>
        </div>
        <LinkModal
          trxId={params?.id}
          trxLinkData={selectedLinkToEdit}
          open={showNewLinkModal}
          closeModal={() => {
            setShowNewLinkModal(false);
            setSelectedLinkToEdit(null);
          }}
          fetchTransactionLinks={fetchTransactionLinks}
        />
        <ConfirmationDialog
          onReject={() => {
            setSelectedLinkToEdit(null);
            setDeleteConfirmation(false);
          }}
          onConfirm={handleDeleteLink}
          isOpen={deleteConfirmation}
          confirmText="Delete"
          isPending={confirmationLoading}
          className={styles.deleteLinkDialog}
        >
          <div className={styles.content}>
            <p className={styles.title}>Delete Link</p>
            Are you sure you want to delete <b>{selectedLinkToEdit?.Name}</b>?
          </div>
        </ConfirmationDialog>
      </Content>
    </PageWrapper>
  );
};
